import { Checkbox, Stack, Typography } from '@mui/material';
import { useInsightsPageParams, useRouter } from '@/hooks';
import { CalendarIcon } from '@/assets/icons/CalendarIcon';
import { Report, ReportActionStatus, ReportType } from '@/__generated__/graphql';
import {
  AssigneeContainer,
  Card,
  ChipLabel,
  DetailLabel,
  DetailValue,
  MainLabel,
  ReportDetail,
  ReportName,
  StatusChip,
} from './styles';
import { NOT_APPLICABLE_LABEL, PoiReportEnumFormattingFilter } from '@/constants';
import { formatEnumForDisplay } from '@/utils';
import { abyssColors } from '@/theme/colors';

type ChipProps = {
  status: ReportActionStatus;
};

const ReportStatusChip = ({ status }: ChipProps) => {
  const StatusDetail = {
    [ReportActionStatus.Backlog]: {
      label: formatEnumForDisplay(ReportActionStatus.Backlog),
      backgroundColor: abyssColors.error[50],
      color: abyssColors.error[600],
    },
    [ReportActionStatus.InProgress]: {
      label: formatEnumForDisplay(ReportActionStatus.InProgress),
      backgroundColor: abyssColors.warning[50],
      color: abyssColors.warning[600],
    },
    [ReportActionStatus.Complete]: {
      label: formatEnumForDisplay(ReportActionStatus.Complete),
      backgroundColor: abyssColors.tertiary[50],
      color: abyssColors.tertiary[600],
    },
  };
  return (
    <StatusChip
      sx={{
        backgroundColor: StatusDetail[status].backgroundColor,
      }}
    >
      <ChipLabel sx={{ color: StatusDetail[status].color }}>{StatusDetail[status].label}</ChipLabel>
    </StatusChip>
  );
};

type AssigneeProps = {
  assignee: ReportType;
};

const AssigneeTeam = ({ assignee }: AssigneeProps) => {
  return (
    <AssigneeContainer>
      <Typography fontSize="1.2rem" fontWeight={500} color={abyssColors.primary[50]}>
        {PoiReportEnumFormattingFilter[assignee]}
      </Typography>
    </AssigneeContainer>
  );
};

type ReportCardProps = {
  report: Report;
  renderCheckBox?: boolean;
  onCheckboxChange?: (reportId: string, isChecked: boolean) => void;
  isChecked?: boolean;
};

export const ReportCard = ({ 
  report, 
  renderCheckBox = false, 
  onCheckboxChange,
  isChecked = false
}: ReportCardProps) => {
  const { getCurrentParams } = useInsightsPageParams();
  const router = useRouter();
  const handleClick = () => {
    if (report?.id) {
      const path = `/analysis/${router.query.inspection}/reports/${report?.id}`
      const currentParams = getCurrentParams()
      router.push({
        pathname: path,
        query: currentParams as Record<string, string>,
      });
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (onCheckboxChange && report?.id) {
      onCheckboxChange(report.id, event.target.checked);
    }
  };

  return (
    <Card onClick={handleClick}>
      <ReportDetail
        marginBottom={2}
        sx={{ justifyContent: renderCheckBox ? 'start' : 'space-between' }}
      >
        {renderCheckBox && (
          <Checkbox
            onChange={handleCheckboxChange}
            checked={isChecked}
            size='medium'
            onClick={(e) => e.stopPropagation()}
            sx={{
              paddingLeft: 0,
              paddingRight: 0,
            }}
            disableRipple
          />
        )}
        <ReportName>{report?.name}</ReportName>
        {report.type && <AssigneeTeam assignee={report?.type} />}
      </ReportDetail>
      <ReportDetail marginBottom={1.75}>
        <MainLabel>Workpack#</MainLabel>
        <Stack direction="row" gap={1} alignItems="center">
          <MainLabel>{report?.workpackNumber ?? NOT_APPLICABLE_LABEL}</MainLabel>
        </Stack>
      </ReportDetail>
      <Stack marginBottom={4} gap={0.75}>
        <ReportDetail>
          <DetailLabel>Status</DetailLabel>
          <ReportStatusChip status={report?.actionStatus ?? ReportActionStatus.Backlog} />
        </ReportDetail>
      </Stack>
      <Stack gap={1}>
        <ReportDetail>
          <Stack direction="row">
            <CalendarIcon />
            <DetailLabel>Start Date</DetailLabel>
          </Stack>
          <DetailValue>
            {report.startDate
              ? new Date(report.startDate).toLocaleDateString('en-GB', {
                  dateStyle: 'medium',
                })
              : NOT_APPLICABLE_LABEL}
          </DetailValue>
        </ReportDetail>
        <ReportDetail>
          <Stack direction="row">
            <CalendarIcon />
            <DetailLabel>End Date</DetailLabel>
          </Stack>
          <DetailValue>
            {report.dueDate
              ? new Date(report.dueDate).toLocaleDateString('en-GB', {
                  dateStyle: 'medium',
                })
              : NOT_APPLICABLE_LABEL}
          </DetailValue>
        </ReportDetail>
      </Stack>
    </Card>
  );
};
